import { inject, injectable } from 'inversify'
import { nanoid } from 'nanoid'
import type { Object3D } from 'three'
import { setup } from 'xstate'
import { ObjectManager } from '@/plugins/cad/module/CADModule/manager/ObjectManager'
import type { IPoint2 } from '@/plugins/cad/module/common/interface'
import { TYPES } from '@/plugins/cad/module/container/types'
import type {
  KeyEventInfoHandler,
  KeyEventParams,
  MouseEventInfoHandler,
  MouseEventParams,
} from '@/plugins/cad/module/core/common/event'
import type { DetectorEventHandler } from '@/plugins/cad/module/core/selector/Detector'
import { SelectMode, type SelectorEventHandler } from '@/plugins/cad/module/core/selector/Selector'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { focusCadTipInput } from '@/plugins/cad/module/utils/focusCadTipInput'
import { styleCadTipNumberInput } from '@/plugins/cad/module/utils/styleCadTipNumberInput'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { KeyCodes } from '@/shared/constants'
import { parseDisplayLength } from '@/shared/unitPresentation'
import { type MouseTipHelper, MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import type { CADController } from '../CADController'
import { OffsetEntityCommand } from '../command/OffsetEntityCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import type { BaseEntity, IEntity } from '../model/entity'
import type { EntitySelector } from '../selector/entitySelector'
import { EntityEditHandler, type OffsetSide } from './EntityEditHandler'
import { updateCadDistanceInputPresentation } from './updateCadDistanceInputPresentation'
import { OffsetUtils } from './utils/offsetUtils'

const offsetEntityMachine = setup({
  actions: {
    entryInputDistance: () => {},
    exitInputDistance: () => {},
    entrySelectEntity: () => {},
    exitSelectEntity: () => {},
    entrySpecifyDirection: () => {},
    exitSpecifyDirection: () => {},
    entryExecute: () => {},
  },
  guards: {
    hasSelectedEntity: () => false,
  },
}).createMachine({
  id: 'offsetEntity',
  initial: 'idle',
  states: {
    idle: {
      on: { NEXT: 'inputDistance' },
    },
    /** 输入偏移距离阶段 */
    inputDistance: {
      entry: { type: 'entryInputDistance' },
      exit: { type: 'exitInputDistance' },
      on: {
        NEXT: 'selectEntity',
        MOUSE_RIGHT_DOWN: 'selectEntity',
      },
    },
    /** 选择偏移对象（单选） */
    selectEntity: {
      entry: { type: 'entrySelectEntity' },
      exit: { type: 'exitSelectEntity' },
      on: {
        NEXT: { target: 'specifyDirection', guard: 'hasSelectedEntity' },
      },
    },
    /** 指定偏移方向 */
    specifyDirection: {
      entry: { type: 'entrySpecifyDirection' },
      exit: { type: 'exitSpecifyDirection' },
      on: { MOUSE_RIGHT_DOWN: 'execute' },
    },
    /** 执行偏移操作 */
    execute: {
      entry: { type: 'entryExecute' },
      on: {
        // 执行偏移后返回 selectEntity 继续偏移
        NEXT: 'selectEntity',
      },
    },
  },
})

/**
 * 偏移实体工具：输入偏移距离后选择实体与方向，按距离与方向生成新实体。
 */
@injectable()
export class OffsetEntityTool
  extends StateMachineTool<typeof offsetEntityMachine>
  implements
    MouseEventInfoHandler,
    KeyEventInfoHandler,
    SelectorEventHandler<IEntity>,
    DetectorEventHandler<IEntity>
{
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.OffsetEntityTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.UnitPresentationService)
  private readonly unitPresentation!: UnitPresentationService

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  protected machine = offsetEntityMachine.provide({
    actions: {
      entryInputDistance: () => this.entryInputDistance(),
      exitInputDistance: () => this.exitInputDistance(),
      entrySelectEntity: () => this.entrySelectEntity(),
      exitSelectEntity: () => this.exitSelectEntity(),
      entrySpecifyDirection: () => this.entrySpecifyDirection(),
      exitSpecifyDirection: () => this.exitSpecifyDirection(),
      entryExecute: () => this.entryExecute(),
    },
    guards: {
      hasSelectedEntity: () => this.hasSelectedEntity(),
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController

  /** 偏移距离（默认 2） */
  private readonly defaultOffsetDistance: number = 2
  private offsetDistance: number = this.defaultOffsetDistance
  /** 偏移方向（1 表示右侧，-1 表示左侧） */
  private offsetSide: OffsetSide = 1
  /** 当前选中的单个实体 */
  private selectedEntity: IEntity | null = null
  /** 鼠标当前世界坐标 */
  private mousePos: IPoint2 = { x: 0, y: 0 }

  /** 预览层 */
  private entityPreviewMap: Map<
    string,
    Map<
      OffsetSide,
      {
        entity: IEntity | null
        object: Object3D | null
      }
    >
  > = new Map() // OffsetSide -> 预览对象

  /** 活动命令（跨多次偏移共用，直到工具退出才提交） */
  private currentCommand: OffsetEntityCommand | null = null
  /** 距离输入框 */
  private inputEl: HTMLInputElement | null = null
  private unsubscribeUnitPreference: (() => void) | null = null
  private onDocumentMouseDown: ((event: MouseEvent) => void) | null = null

  protected onInitialize(): void {
    this.entitySelector.clearSelection()
    this.entitySelector.config({ enableFrameSelect: false, mode: SelectMode.replace }) // 关闭框选功能，仅支持同时选中一个图元

    this.currentCommand = new OffsetEntityCommand(this.viewManager)
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.entitySelector.clearSelection()
    this.entitySelector.resetConfig()
    this.mouseTipHelper.removeTip(MouseTipId.Edit)
    this.removeInputEl()
    this.clearEntityPreviewMap()

    this.selectedEntity = null
    this.currentCommand = null
    this.offsetDistance = this.defaultOffsetDistance
    this.offsetSide = 1
  }

  /**
   * 鼠标按下：右键在各阶段触发 MOUSE_RIGHT_DOWN 事件。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 2) {
      this.send({ type: 'MOUSE_RIGHT_DOWN' })
    }
  }

  /**
   * 鼠标移动：方向阶段更新偏移预览。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    this.mousePos = { x: params.worldPos.x, y: params.worldPos.y }
    if (this.checkState('specifyDirection')) {
      this.updateCanvas()
    }
  }

  /**
   * 键盘按下：ESC 退出；输入距离阶段 ENTER 确认距离。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
      return
    }
    if (this.checkState('inputDistance') && params.event.keyCode === KeyCodes.ENTER) {
      this.send({ type: 'NEXT' })
    }
  }

  /**
   * 选择变化回调：记录当前要偏移的实体并推进到方向阶段。
   *
   * @param entities - 当前已选中的实体
   */
  onSelectorChanged(entities: IEntity[]) {
    if (entities.length > 0) {
      this.selectedEntity = entities[0]
      // 选中后立即推进到 specifyDirection
      this.send({ type: 'NEXT' })
    } else {
      this.selectedEntity = null
    }
  }

  /**
   * 实体探测过滤：仅保留支持偏移的类型。
   *
   * @param entities - 候选实体列表
   * @returns 过滤后可偏移的实体列表
   */
  onDetectorFilter(entities: IEntity[]): IEntity[] {
    return entities.filter((e) => OffsetUtils.EnabledEntityTypes.has(e.type))
  }

  /************ 状态机状态处理 ************/
  private hasSelectedEntity() {
    return this.selectedEntity !== null
  }

  private entryInputDistance() {
    this.showInputEl()
  }

  private exitInputDistance() {
    this.mouseTipHelper.removeTip(MouseTipId.Edit)
    this.removeInputEl()
  }

  private entrySelectEntity() {
    this.selectedEntity = null
    this.entitySelector.clearSelection()
    this.inject(this.entitySelector)
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.offset.target)
  }

  private exitSelectEntity() {
    this.eject(this.entitySelector)
  }

  private entrySpecifyDirection() {
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.offset.direction)
  }

  private exitSpecifyDirection() {}

  private entryExecute() {
    this.executeOffset()
  }

  /************ 输入框处理 ************/
  private showInputEl() {
    this.mouseTipHelper.removeTip(MouseTipId.Edit)
    this.removeInputEl()
    this.mouseTipHelper.addCustomTip(MouseTipId.Edit, (translate) =>
      this.createDistanceTipElement(translate)
    )
    if (this.inputEl) {
      focusCadTipInput(this.inputEl, true)
    }
  }

  private createDistanceTipElement(translate: (key: string) => string) {
    const element = document.createElement('span')
    element.innerText = translate(CadTipKey.edit.offset.distanceLabel)
    element.style =
      'background-color: #fff; color: black; padding: 4px; font-size: 12px; white-space: nowrap;'
    const inputElement = document.createElement('input')
    inputElement.type = 'number'
    styleCadTipNumberInput(inputElement)
    inputElement.style.width = '60px'
    updateCadDistanceInputPresentation(
      inputElement,
      this.offsetDistance,
      this.unitPresentation.preference
    )
    inputElement.addEventListener('change', () => {
      const value = parseDisplayLength(inputElement.value, this.unitPresentation.preference)
      if (value === null || value <= 0) {
        this.offsetDistance = this.defaultOffsetDistance
        updateCadDistanceInputPresentation(
          inputElement,
          this.defaultOffsetDistance,
          this.unitPresentation.preference
        )
      } else {
        this.offsetDistance = value
      }
    })
    inputElement.addEventListener('mousedown', (e) => {
      if (e.button === 2) {
        e.stopPropagation()
        e.preventDefault()
        this.send({ type: 'NEXT' })
      }
    })
    if (this.onDocumentMouseDown) {
      document.removeEventListener('mousedown', this.onDocumentMouseDown, false)
      this.onDocumentMouseDown = null
    }
    this.onDocumentMouseDown = (event: MouseEvent) => {
      // 检查点击的元素是否不是 input 本身, 如果不是，则阻止默认行为并聚焦到 input 上
      if (event.target !== inputElement) {
        event.preventDefault()
        focusCadTipInput(inputElement, true)
      }
    }
    document.addEventListener('mousedown', this.onDocumentMouseDown, false)
    element.appendChild(inputElement)
    this.inputEl = inputElement
    this.unsubscribeUnitPreference = this.unitPresentation.subscribe(() => {
      updateCadDistanceInputPresentation(
        inputElement,
        this.offsetDistance,
        this.unitPresentation.preference
      )
    })
    return element
  }

  private removeInputEl() {
    this.unsubscribeUnitPreference?.()
    this.unsubscribeUnitPreference = null
    if (this.inputEl) {
      this.inputEl.parentElement?.removeChild(this.inputEl)
      this.inputEl = null
    }
    if (this.onDocumentMouseDown) {
      document.removeEventListener('mousedown', this.onDocumentMouseDown, false)
      this.onDocumentMouseDown = null
    }
  }

  /************ 预览渲染 ************/
  private updateCanvas() {
    if (!this.selectedEntity) {
      return
    }
    this.offsetSide = this.computeSide()
    const entityId = this.selectedEntity.id
    if (!this.entityPreviewMap.has(entityId)) {
      this.entityPreviewMap.set(entityId, new Map())
    }
    const previewMap = this.entityPreviewMap.get(entityId)!
    const previewData = previewMap.get(this.offsetSide)
    previewMap?.forEach((data, side) => {
      if (side !== this.offsetSide && data.object) {
        data.object.visible = false // 只显示当前偏移方向的预览对象
      }
    })
    if (previewData) {
      previewData.object && (previewData.object.visible = true)
      return // 已存在预览对象，无需重新生成
    }
    // 预览使用 EntityEditHandler 的 offset 方法生成一个临时实体进行渲染，真正的偏移操作在 entryExecute 中执行
    const entity = EntityEditHandler.offset(this.selectedEntity, {
      distance: this.offsetDistance,
      side: this.offsetSide,
    })
    const manager = this.getPreviewObjectManager()
    entity && manager.addEntity(entity)
    previewMap.set(this.offsetSide, {
      entity,
      object: entity ? manager.getEntityObject(entity) || null : null,
    })
  }

  private clearEntityPreviewMap() {
    this._previewObjectManager?.dispose()
    this._previewObjectManager?.getSceneGroup().removeFromParent()
    this._previewObjectManager = undefined
    this.entityPreviewMap.clear()
  }

  private _previewObjectManager?: ObjectManager

  private getPreviewObjectManager(): ObjectManager {
    if (!this._previewObjectManager) {
      this._previewObjectManager = new ObjectManager()
      this.viewManager.attachTempObject(this._previewObjectManager.getSceneGroup())
    }
    return this._previewObjectManager
  }

  private hidePreview(entityId: string, side: OffsetSide) {
    const previewMap = this.entityPreviewMap.get(entityId)
    const previewData = previewMap?.get(side)
    if (previewData?.object) {
      previewData.object.visible = false
    }
  }

  /************ 偏移方向计算 ************/
  private computeSide(): OffsetSide {
    if (!this.selectedEntity) {
      return 1
    }
    return OffsetUtils.computeSide(this.selectedEntity, this.mousePos)
  }

  /************ 执行偏移 ************/
  private executeOffset() {
    if (!this.selectedEntity || !this.currentCommand) {
      return
    }
    const previewMap = this.entityPreviewMap.get(this.selectedEntity.id)
    const previewData = previewMap?.get(this.offsetSide)
    this.hidePreview(this.selectedEntity.id, this.offsetSide)
    this.offsetSide = 1 // 重置默认偏移方向
    if (previewData?.entity) {
      const newEntity = (previewData.entity as BaseEntity).clone()
      newEntity.id = nanoid()
      this.currentCommand.addEntity(newEntity)
    }
    // 偏移完成后继续回到选实体步骤
    this.send({ type: 'NEXT' })
  }
}
